<!-- eslint-disable vue/no-parsing-error -->
<template>
  <div class="p-5 flex flex-col">
    <p class="font-semibold text-2xl">
      {{ this.$route.meta.title }}
    </p>
    <vs-row
      vs-w="12"
      class="my-5 filter-container"
    >
      <vs-col vs-w="1">
        <p class="font-semibold">
          Filter
        </p>
      </vs-col>
      <div class="flex-grow" />
      <vs-col vs-w="2">
        <select-with-search
          :hide-search="true"
          v-model="filters.site"
          placeholder="Select Site"
          class="px-2"
          :options="siteOptions"
        />
      </vs-col>
      <vs-col vs-w="1.5">
        <date-picker
          v-model="filters.year"
          :format="'YYYY'"
          type="year"
          value-type="YYYY"
          placeholder="Year"
          class="px-2"
        />
      </vs-col>
      <vs-col
        vs-w="1.5"
        class="flex flex-col justify-center"
      >
        <vs-button
          type="border"
          color="primary"
          class="self-center font-semibold"
          style="min-width: 100px;"
          @click="resetFilters"
        >
          Reset
        </vs-button>
      </vs-col>
    </vs-row>

    <!-- Q: Quantity & Quality -->
    <div class="mt-3 font-semibold">
      Q: Quantity & Quality
    </div>
    <div
      v-if="isBusy"
      class="p-3 w-full flex flex-col gap-5 bg-white rounded-lg shadow-md mt-3"
    >
      <vs-progress
        indeterminate
        color="primary"
      >
        primary
      </vs-progress>
    </div>
    <div
      v-else
      class="mt-3 grid grid-cols-2 gap-3"
    >
      <MetricCard
        :title="`Plan Tonnage Move ${filters.year}`"
        :value="qualityAndQuantity.planTonnageMove"
        unit="Ton"
      />
      <MetricCard
        :title="`Total Tonnage Move ${filters.year}`"
        :value="qualityAndQuantity.totalTonnageMove"
        unit="Ton"
        highlight
      />
    </div>
    <div class="mt-3 p-4 bg-white rounded-lg shadow">
      <h2 class="text-lg font-semibold mb-4">
        Tonnage Move
      </h2>
      <vs-progress
        v-if="isBusy"
        indeterminate
        color="primary"
      >
        primary
      </vs-progress>
      <BarChart
        v-else
        :data="qualityAndQuantity.chartTonnageMove"
        :options="chartOptions('Ton')"
        :plugins="[]"
      />
    </div>
    <div class="mt-3 p-4 bg-white rounded-lg shadow">
      <h2 class="text-lg font-semibold mb-4">
        Return Cargo, Cargo Loss, Stolen Coal
      </h2>
      <vs-progress
        v-if="isBusy"
        indeterminate
        color="primary"
      >
        primary
      </vs-progress>
      <BarChart
        v-else
        :data="qualityAndQuantity.chartCargo"
        :options="chartOptions('Ton')"
        :plugins="[]"
      />
    </div>

    <!-- C: Cost -->
    <div class="mt-3 font-semibold">
      C: Cost
    </div>
    <div
      v-if="isBusy"
      class="p-3 w-full flex flex-col gap-5 bg-white rounded-lg shadow-md mt-3"
    >
      <vs-progress
        indeterminate
        color="primary"
      >
        primary
      </vs-progress>
    </div>
    <div
      v-else
      class="mt-3 grid grid-cols-2 gap-3"
    >
      <MetricCard
        :title="`Plan Barging Cost ${filters.year}`"
        :value="cost.planBargingCost"
        unit="USD/Ton"
      />
      <MetricCard
        :title="`Total Barging Cost ${filters.year}`"
        :value="cost.totalBargingCost"
        unit="USD/Ton"
        highlight
      />
    </div>
    <div class="mt-3 p-4 bg-white rounded-lg shadow">
      <h2 class="text-lg font-semibold mb-4">
        Barging Cost
      </h2>
      <vs-progress
        v-if="isBusy"
        indeterminate
        color="primary"
      >
        primary
      </vs-progress>
      <BarChart
        v-else
        :data="cost.chartBargingCost"
        :options="chartOptions('USD/Ton')"
        :plugins="[]"
      />
    </div>

    <!-- T: Time -->
    <div class="mt-3 font-semibold">
      T: Time
    </div>
    <div
      v-if="isBusy"
      class="p-3 w-full flex flex-col gap-5 bg-white rounded-lg shadow-md mt-3"
    >
      <vs-progress
        indeterminate
        color="primary"
      >
        primary
      </vs-progress>
    </div>
    <div
      v-else
      class="mt-3 grid grid-cols-2 gap-3"
    >
      <MetricCard
        :title="`Plan Fleet Utilization Route ${filters.year}`"
        :value="time.planFleetUtilization"
        unit="%"
      />
      <MetricCard
        :title="`Total Fleet Utilization Route ${filters.year}`"
        :value="time.totalFleetUtilization"
        unit="%"
        highlight
      />
    </div>
    <div class="mt-3 p-4 bg-white rounded-lg shadow">
      <h2 class="text-lg font-semibold mb-4">
        Barge Route
      </h2>
      <vs-progress
        v-if="isBusy"
        indeterminate
        color="primary"
      >
        primary
      </vs-progress>
      <BarChart
        v-else
        :data="time.chartBargeRoute"
        :options="chartOptions('Days')"
        :plugins="[]"
      />
    </div>

    <!-- S: Safety -->
    <div class="mt-3 font-semibold">
      S: Safety
    </div>
    <div class="mt-3 p-4 bg-white rounded-lg shadow">
      <h2 class="text-lg font-semibold mb-4">
        Accident
      </h2>
      <vs-progress
        v-if="isBusy"
        indeterminate
        color="primary"
      >
        primary
      </vs-progress>
      <BarChart
        v-else
        :data="safety.chartAccident"
        :options="chartOptions('Times')"
        :plugins="[]"
      />
    </div>

    <!-- E: Energy Saving -->
    <div class="mt-3 font-semibold">
      E: Energy Saving
    </div>
    <div
      v-if="isBusy"
      class="p-3 w-full flex flex-col gap-5 bg-white rounded-lg shadow-md mt-3"
    >
      <vs-progress
        indeterminate
        color="primary"
      >
        primary
      </vs-progress>
    </div>
    <div
      v-else
      class="mt-3 grid grid-cols-2 gap-3"
    >
      <MetricCard
        title="Avg. Fuel Consumption"
        :value="energy.avgFuelConsumption"
        unit="L/Ton"
      />
      <MetricCard
        title="Total Fuel Supplied"
        :value="energy.totalFuelSupplied"
        unit="Lt"
      />
    </div>
    <div class="mt-3 p-4 bg-white rounded-lg shadow">
      <h2 class="text-lg font-semibold mb-4">
        Fuel Supplied
      </h2>
      <vs-progress
        v-if="isBusy"
        indeterminate
        color="primary"
      >
        primary
      </vs-progress>
      <BarChart
        v-else
        :data="energy.chartFuelSupplied"
        :options="chartOptions('L/Ton')"
        :plugins="[]"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

import BarChart from "@/components/barChart"
import DatePicker from "@/components/datePicker";
import MetricCard from "@/components/metricCard";
import SelectWithSearch from "@/components/selectWithSearch";
import { PAGE_TITLE } from "@/fixtures/Constants";
export default {
  components: {
    BarChart,
    DatePicker,
    MetricCard,
    SelectWithSearch
  },
  metaInfo() {
    return {
      title: `${PAGE_TITLE} ${this.$route.meta.title}`
    }
  },
  data() {
    const currentYear = this.moment().format('YYYY')
    return {
      isBusy: false,
      usedColors: new Set(),
      filters: {
        site: null,
        year: currentYear
      },
      siteOptions: [],
      qualityAndQuantity: {
        planTonnageMove: 0,
        totalTonnageMove: 0,
        chartTonnageMove: {
          labels: [],
          datasets: []
        },
        chartCargo: {
          labels: [],
          datasets: []
        }
      },
      tonnageMoveDatasets: [
        {
          key: 'freight_basis_plan',
          label: 'Freight Basis Plan',
          stack: 'plan',
          color: '#A52A2A'
        },
        {
          key: 'time_charter_plan',
          label: 'Time Charter Plan',
          stack: 'plan',
          color: '#FFD700'
        },
        {
          key: 'freight_basis_actual',
          label: 'Freight Basis Actual',
          stack: 'actual',
          color: '#CD5C5C'
        },
        {
          key: 'time_charter_actual',
          label: 'Time Charter Actual',
          stack: 'actual',
          color: '#FFA500'
        }
      ],
      cargoDatasets: [
        {
          key: 'stolen_coal',
          label: 'Stolen Coal',
          color: '#DA7678'
        },
        {
          key: 'cargo_loss',
          label: 'Cargo Loss',
          color: '#DC3134'
        },
        {
          key: 'return_cargo',
          label: 'Return Cargo',
          color: '#8C1545'
        }
      ],
      cost: {
        planBargingCost: 0,
        totalBargingCost: 0,
        chartBargingCost: {
          labels: [],
          datasets: []
        }
      },
      bargingCostDatasets: [
        {
          key: 'freight_cost_per_ton',
          label: 'Freight Basic Cost Plan',
          stack: 'plan',
          color: '#348BC8'
        },
        {
          key: 'fuel_supply_service_cost_per_ton',
          label: 'Fuel Supply Service Plan',
          stack: 'plan',
          color: '#74C834'
        },
        {
          key: 'fuel_cost_per_ton',
          label: 'Fuel Cost Plan',
          stack: 'plan',
          color: '#CF6828'
        },
        {
          key: 'hiring_cost_per_ton',
          label: 'Hiring Cost Plan',
          stack: 'plan',
          color: '#FFCF0F'
        },
        {
          key: 'freight_cost_per_ton',
          label: 'Freight Basic Cost Actual',
          stack: 'actual',
          color: '#3440C8'
        },
        {
          key: 'gov_gen_expense_per_ton',
          label: 'Gov & Gen Expense Actual',
          stack: 'actual',
          color: '#51268D'
        },
        {
          key: 'fuel_supply_service_cost_per_ton',
          label: 'Fuel Supply Service Actual',
          stack: 'actual',
          color: '#34C83E'
        },
        {
          key: 'fuel_cost_per_ton',
          label: 'Fuel Cost Actual',
          stack: 'actual',
          color: '#89451A'
        },
        {
          key: 'hiring_cost_per_ton',
          label: 'Hiring Cost Actual',
          stack: 'actual',
          color: '#D1A700'
        }
      ],
      time: {
        planFleetUtilization: 0,
        totalFleetUtilization: 0,
        chartBargeRoute: {
          labels: [],
          datasets: []
        },
        chartOnTime: {
          labels: [],
          datasets: []
        }
      },
      safety: {
        fatal: 0,
        injuredWounded: 0,
        chartAccident: {
          labels: [],
          datasets: []
        }
      },
      energy: {
        avgFuelConsumption: 0,
        totalFuelSupplied: 0,
        chartFuelSupplied: {
          labels: [],
          datasets: []
        }
      }
    }
  },
  computed: {
    ...mapGetters({
      qctseData: 'BargeMeasurementPerformance/qctseData',
      companies: 'Companies/companies'
    })
  },
  watch: {
    companies: {
      handler(val) {
        if (val) {
          this.siteOptions = val.map(item => ({
            value: item.code,
            text: item.code
          }))
        }
      }
    },
    qctseData: {
      handler(val) {
        if (!this.isEmpty(val)) {
          // Populate Q
          const tempQ = val.quality_and_quantity
          this.qualityAndQuantity.planTonnageMove = tempQ?.plan_tonnage_move
          this.qualityAndQuantity.totalTonnageMove = tempQ?.total_tonnage_move
          this.qualityAndQuantity.chartTonnageMove = {
            labels: this.populateDataMonth(tempQ?.tonnage_move_bar_chart?.length, tempQ?.tonnage_move_bar_chart?.[0]?.month),
            datasets: this.tonnageMoveDatasets.map(({ key, label, stack, color }) => ({
              label: label,
              backgroundColor: color,
              data: tempQ?.tonnage_move_bar_chart?.map(v => v[key]),
              stack: stack,
              barPercentage: 0.6,
              categoryPercentage: 0.6,
              borderRadius: 5
            }))
          }
          this.qualityAndQuantity.chartCargo = {
            labels: this.populateDataMonth(tempQ?.cargo?.length, tempQ?.cargo?.[0]?.month),
            datasets: this.cargoDatasets.map(({ key, label, color }) => ({
              label: label,
              backgroundColor: color,
              data: tempQ?.cargo?.map(v => v[key]),
              stack: 'plan',
              barPercentage: 0.6,
              categoryPercentage: 0.6,
              borderRadius: 5
            }))
          }

          // Populate C
          const tempC = val.cost
          this.cost.planBargingCost = tempC?.summary?.plan_barging_cost_per_ton
          this.cost.totalBargingCost = tempC?.summary?.total_barging_cost_per_ton
          this.cost.chartBargingCost = {
            labels: this.populateDataMonth(tempC?.actual?.length, tempC?.actual?.[0]?.month),
            datasets: this.bargingCostDatasets.map(({ key, label, stack, color }) => ({
              label: label,
              backgroundColor: color,
              data: tempC?.[stack]?.map(v => v[key]),
              stack: stack,
              barPercentage: 0.6,
              categoryPercentage: 0.6,
              borderRadius: 5
            }))
          }

          // Populate T
          const tempT = val.time
          this.time.planFleetUtilization = tempT?.summary?.plan_fleet_utilization
          this.time.totalFleetUtilization = tempT?.summary?.total_fleet_utilization
          this.time.chartBargeRoute = {
            labels: this.populateDataMonth(tempT?.bar_chart?.length, tempT?.bar_chart?.[0]?.month),
            datasets: [
              ...tempT?.bar_chart?.length ? tempT.bar_chart?.[0]?.data?.map(v => ({
                label: `${v?.route_name} Plan`,
                data: tempT.bar_chart?.map(d =>
                  parseFloat(
                    d?.data?.find(k => k?.route_name === v?.route_name)
                      ?.plan_days
                  ).toFixed(2)
                ),
                backgroundColor: this.getRandomColor(1),
                stack: 'plan',
                barPercentage: 0.6,
                categoryPercentage: 0.6,
                borderRadius: 5
              })) : [],
              ...tempT?.bar_chart?.length ?tempT.bar_chart?.[0]?.data?.map(v => ({
                label: `${v?.route_name} Actual`,
                data: tempT.bar_chart?.map(d =>
                  parseFloat(
                    d?.data?.find(k => k?.route_name === v?.route_name)
                      ?.actual_days
                  ).toFixed(2)
                ),
                backgroundColor: this.getRandomColor(1),
                stack: 'actual',
                barPercentage: 0.6,
                categoryPercentage: 0.6,
                borderRadius: 5
              })) : []
            ]
          }
          this.time.chartOnTime = {
            labels: [],
            datasets: []
          }

          // Populate S
          const tempS = val.safety
          const objAccident = this.mergeUniqueActivities(tempS.bar_chart, 'activity_name')
          this.safety.fatal = tempS?.summary?.fatal
          this.safety.injuredWounded = tempS?.summary?.injured_wounded
          this.safety.chartAccident = {
            labels: this.populateDataMonth(tempS?.bar_chart?.length, tempS?.bar_chart?.[0]?.month),
            datasets: objAccident.map(v => ({
              label: v.activity_name,
              backgroundColor: this.getRandomColor(1),
              data: tempS.bar_chart?.map(d => d?.data?.find(k => k?.activity_name === v.activity_name)?.total),
              stack: 'plan',
              barPercentage: 0.6,
              categoryPercentage: 0.6,
              borderRadius: 5
            }))
          }

          // Populate E
          const tempE = val.energy
          const objFuelSupplied = this.mergeUniqueActivities(tempE.bar_chart, 'route')
          this.energy.avgFuelConsumption = tempE?.summary?.avg_fuel_consumption
          this.energy.totalFuelSupplied = tempE?.summary?.total_fuel_supplied
          this.energy.chartFuelSupplied = {
            labels: this.populateDataMonth(tempE?.bar_chart?.length, tempE?.bar_chart?.[0]?.month),
            datasets: objFuelSupplied.map(v => ({
              label: v.route,
              backgroundColor: this.getRandomColor(1),
              data: tempE.bar_chart?.map(d => d?.data?.find(k => k?.route === v.route)?.value),
              stack: 'actual',
              barPercentage: 0.6,
              categoryPercentage: 0.6,
              borderRadius: 5
            }))
          }
        }
        else {
          this.resetState()
        }
      }
    },
    filters: {
      deep: true,
      handler(val) {
        if (val.month || val.year) {
          this.fetchSite()
        }
        this.fetchBargeMeasurementPerformance()
      }
    }
  },
  mounted() {
    this.fetchBargeMeasurementPerformance()
    this.fetchSite()
  },
  methods: {
    chartOptions(title) {
      return {
        responsive: true,
        layout: {
          padding: {
            top: 30,
            right: 30,
            bottom: 30
          }
        },
        scales: {
          xAxes: [{ stacked: true }],
          yAxes: {
            stacked: true,
            ticks: {
              beginAtZero: true,
              precision: 0,
              callback: function(value, index, ticks) {
                const last = ticks[ticks.length - 1];
                if (value === last.value) {
                  return title;
                }
                return value.toLocaleString('id-ID');
              },
              font: {
                family: 'Poppins',
                size: 12,
                weight: '600'
              }
            },
            afterBuildTicks: function(axis) {
              const ticks = axis.ticks;
              if (ticks.length < 2) return;
              const step = ticks[1].value - ticks[0].value;
              const lastValue = ticks[ticks.length - 1].value;
              const extraValue = lastValue + step;
              ticks.push({
                value: extraValue,
                label: extraValue.toString()
              });
              axis.max = extraValue;
            }
          }
        },
        plugins: {
          legend: { position: 'bottom' }
        }
      }
    },
    async fetchBargeMeasurementPerformance() {
      this.isBusy = true
      try {
        const apiFilters = Object.fromEntries(
          Object.entries(this.filters || {}).filter(([_, value]) => !!value)
        )
        await this.$store.dispatch('BargeMeasurementPerformance/fetchBargeMeasurementPerformance', apiFilters)
        this.isBusy = false
      } catch (error) {
        this.isBusy = false
        this.showFailedNotification(this, 'Failed to get  Data, please try again')
      }
    },
    async fetchSite() {
      try {
        await this.$store.dispatch('Companies/fetchCompanies', {
          is_master_data: true,
          type: 'SITE_OWNER',
          status: true,
          sort_dir: 'asc',
          sort_by: 'name',
          limit:null,
          page: null
        })
      } catch (e) {
        this.showFailedNotification(this, 'Failed to fetch companies, please try again')
      }
    },
    populateDataMonth(length, startFrom = 1) {
      if (!length) {
        return []
      }
      return Array.from({ length }, (_, i) =>
        this.moment().month((startFrom - 1 + i) % 12).format('MMM')
      )
    },
    getRandomColor(alpha = 0.7) {
      let color;
      do {
        const r = Math.floor(Math.random() * 156) + 100;
        const g = Math.floor(Math.random() * 156) + 100;
        const b = Math.floor(Math.random() * 156) + 100;
        color = `rgba(${r},${g},${b},${alpha})`;
      } while (this.usedColors.has(color));

      this.usedColors.add(color);
      return color;
    },
    mergeUniqueActivities(data, attributeName) {
      const seen = new Set();
      const merged = [];

      data.forEach(item => {
        item.data.forEach(entry => {
          if (!seen.has(entry[attributeName])) {
            seen.add(entry[attributeName]);
            merged.push(entry);
          }
        });
      });

      return merged;
    },
    resetFilters() {
      this.filters = {
        site: null,
        year: this.moment().format('YYYY')
      }
    },
    resetState() {
      this.qualityAndQuantity = {
        planTonnageMove: 0,
        totalTonnageMove: 0,
        chartTonnageMove: {
          labels: [],
          datasets: []
        },
        chartCargo: {
          labels: [],
          datasets: []
        }
      }
      this.cost = {
        planBargingCost: 0,
        totalBargingCost: 0,
        chartBargingCost: {
          labels: [],
          datasets: []
        }
      }
      this.time = {
        planFleetUtilization: 0,
        totalFleetUtilization: 0,
        chartBargeRoute: {
          labels: [],
          datasets: []
        },
        chartOnTime: {
          labels: [],
          datasets: []
        }
      }
      this.safety = {
        fatal: 0,
        injuredWounded: 0,
        chartAccident: {
          labels: [],
          datasets: []
        }
      }
      this.energy = {
        avgFuelConsumption: 0,
        totalFuelSupplied: 0,
        chartFuelSupplied: {
          labels: [],
          datasets: []
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.filter-container {
  @apply w-full p-3 mt-5 flex flex-row items-center bg-white rounded-lg;
}
</style>
